import type { Call } from '@twilio/voice-sdk';
import { getCallQueueAssignment } from '@/services/app/tenant/call-queue';
import { QueueItem } from '@/contexts/CallbackQueueContext';

/**
 * Post-call prompt plumbing for callback-queue calls.
 *
 * Call Back marks the row in_progress and dials. When the CSR's leg ends, we re-check the
 * row: if the backend already closed it (the Twilio customer-leg status callback re-queues
 * busy / no-answer / failed dials on its own), nothing happens. If it is still in_progress
 * the call connected, and only the CSR knows whether it was resolved or went to voicemail —
 * so CallbackPostCallPrompt asks them, right where they are, instead of leaving the row to
 * be found later on the Call Board.
 */
const EVENT = 'callbackPostCallPrompt';

// Grace period after the CSR leg drops. For an unanswered dial the backend ends the CSR
// leg *after* it has re-queued the row, but a CSR hanging up early (customer leg
// 'canceled') races the Twilio status callback — wait it out before deciding.
const SETTLE_MS = 2500;

export function watchCallbackCall(call: Call | undefined, item: QueueItem) {
  if (!call) return;
  let fired = false;
  const onEnd = () => {
    if (fired) return;
    fired = true;
    window.setTimeout(async () => {
      try {
        const fresh = await getCallQueueAssignment(item.id);
        if (!fresh || fresh.status !== 'in_progress') return;
        window.dispatchEvent(new CustomEvent<QueueItem>(EVENT, { detail: { ...item, ...fresh } }));
      } catch {
        // Leave it to the Call Board's In Progress actions.
      }
    }, SETTLE_MS);
  };
  call.on('disconnect', onEnd);
  call.on('cancel', onEnd);
}

/** Subscribe to prompt requests; returns an unsubscribe function. */
export function onPostCallPrompt(handler: (item: QueueItem) => void) {
  if (typeof window === 'undefined') return () => {};
  const listener = (e: Event) => handler((e as CustomEvent<QueueItem>).detail);
  window.addEventListener(EVENT, listener);
  return () => window.removeEventListener(EVENT, listener);
}
